/**
 * NFC Solutions — platform detection & Capacitor bridge.
 *
 * The React application is used in three shells:
 *
 *   1. Browser (customer QR ordering, desktop administration)
 *      → runs on webnfcs.com in a normal Chrome/Safari tab. Service
 *        Worker registers, PWA install prompts are allowed, no native
 *        bridge available.
 *
 *   2. PWA (legacy — being deprecated for restaurant devices, but still
 *      installable from the desktop browser as a light-weight shell)
 *      → `display-mode: standalone` matches, but `window.Capacitor`
 *        is NOT defined. Same code path as browser today.
 *
 *   3. Capacitor Android APK (restaurant tablets)
 *      → `window.Capacitor.isNativePlatform()` returns true. Native
 *        bridge is available. Service Worker registration is skipped
 *        because the WebView already ships the built assets. PWA
 *        install prompts must be suppressed. Deep-link intents
 *        (https://webnfcs.com/apk/install/<token> or
 *        nfcsolutions://install?token=...) are surfaced via the
 *        @capacitor/app plugin.
 *
 * This module is intentionally dependency-light: it must be safe to
 * import from anywhere (including hot paths in App.js) and must never
 * throw when the Capacitor bridge is not present.
 */

import { App as CapApp } from "@capacitor/app";

let _cached = null;

/**
 * @returns {{
 *   isNative: boolean,     // running inside Capacitor
 *   isAndroid: boolean,    // Capacitor + Android specifically
 *   isIos: boolean,        // reserved — not shipped yet
 *   isBrowser: boolean,    // opposite of isNative
 *   isStandalonePwa: boolean, // display-mode: standalone but NOT Capacitor
 *   platform: "apk"|"web"|"pwa",  // build-time flag from REACT_APP_PLATFORM
 *   appVersion: string,
 *   backendUrl: string,
 * }}
 */
export function getPlatform() {
  if (_cached) return _cached;

  const cap = typeof window !== "undefined" ? window.Capacitor : null;
  const isNative = !!(cap && typeof cap.isNativePlatform === "function" && cap.isNativePlatform());
  const capPlatform = isNative && typeof cap.getPlatform === "function" ? cap.getPlatform() : "";
  const isAndroid = isNative && capPlatform === "android";
  const isIos = isNative && capPlatform === "ios";

  let isStandalonePwa = false;
  try {
    if (!isNative && typeof window !== "undefined" && window.matchMedia) {
      isStandalonePwa = window.matchMedia("(display-mode: standalone)").matches
        || window.navigator?.standalone === true;
    }
  } catch (_) { /* noop */ }

  const buildFlag = (process.env.REACT_APP_PLATFORM || "").toLowerCase();
  const platform = buildFlag === "apk" || isNative
    ? "apk"
    : (isStandalonePwa ? "pwa" : "web");

  _cached = {
    isNative,
    isAndroid,
    isIos,
    isBrowser: !isNative,
    isStandalonePwa,
    platform,
    appVersion: process.env.REACT_APP_VERSION || "0.0.0",
    backendUrl: process.env.REACT_APP_BACKEND_URL || "",
  };
  return _cached;
}

/** Cheap boolean helpers for call-sites that only care about one thing. */
export const isNativeApp = () => getPlatform().isNative;
export const isAndroidApp = () => getPlatform().isAndroid;
export const isBrowser = () => getPlatform().isBrowser;

/**
 * Parse an install-link URL into `{ token, kind }`.
 * Accepted shapes:
 *   - https://<any-host>/apk/install/<token>[?...]
 *   - https://<any-host>/install/<token>[?...]
 *   - nfcsolutions://install?token=<token>
 *   - nfcsolutions://install/<token>
 *   - a bare token string, in which case { token } is returned as-is.
 * Returns `null` if no token could be extracted.
 */
export function parseInstallUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Custom scheme
  if (/^nfcsolutions:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      const qsToken = url.searchParams.get("token");
      if (qsToken) return { token: qsToken, kind: "custom-scheme" };
      const seg = url.pathname.replace(/^\/+/, "").split("/").filter(Boolean);
      const t = seg[seg.length - 1];
      if (t) return { token: t, kind: "custom-scheme" };
    } catch (_) { /* fallthrough */ }
  }

  // https://.../apk/install/<token> OR https://.../install/<token>
  try {
    const url = new URL(trimmed);
    const m = url.pathname.match(/\/(?:apk\/)?install\/([A-Za-z0-9._~-]+)\/?$/);
    if (m) return { token: m[1], kind: "https" };
    // token in query string
    const qsToken = url.searchParams.get("token");
    if (qsToken) return { token: qsToken, kind: "https" };
  } catch (_) { /* not a URL */ }

  // Assume it's a raw token if it looks token-shaped (>=8 chars, no whitespace)
  if (/^[A-Za-z0-9._~-]{8,}$/.test(trimmed)) {
    return { token: trimmed, kind: "raw" };
  }

  return null;
}

/**
 * Subscribe to inbound deep-link intents delivered by @capacitor/app.
 * Fires for both cold-launch (the tap that opened the APK) and warm
 * launches (the app already in memory receives a new intent).
 *
 * On the browser this is a no-op — no bridge available.
 *
 * @param {(payload: { token: string, source: string, url: string }) => void} handler
 * @returns {() => void} unsubscribe function
 */
export async function onInstallLinkIntent(handler) {
  const p = getPlatform();
  if (!p.isNative) return () => {};
  if (typeof handler !== "function") return () => {};

  // 1) Cold-launch URL — Capacitor buffers the launch intent for us.
  try {
    const launch = await CapApp.getLaunchUrl();
    const parsed = parseInstallUrl(launch?.url || "");
    if (parsed?.token) {
      handler({ token: parsed.token, source: "cold-launch", url: launch.url });
    }
  } catch (_) { /* noop */ }

  // 2) Warm-launch URL — future intents while the app is running.
  let listener = null;
  try {
    listener = await CapApp.addListener("appUrlOpen", (event) => {
      const parsed = parseInstallUrl(event?.url || "");
      if (parsed?.token) {
        handler({ token: parsed.token, source: "app-url-open", url: event.url });
      }
    });
  } catch (_) { /* noop */ }

  return () => {
    try { listener?.remove?.(); } catch (_) { /* noop */ }
  };
}

/**
 * Best-effort exit / back handler for hardware-back on Android. The
 * default behaviour would exit the app when the back button is
 * pressed at the top of the history stack. For a POS the correct UX
 * is to no-op or ask for confirmation — we register a handler here
 * that lets React Router take precedence for in-app navigation, and
 * only exits when React would otherwise leave the app.
 */
export async function attachHardwareBack(onBackAtRoot) {
  const p = getPlatform();
  if (!p.isAndroid) return () => {};

  let listener = null;
  try {
    listener = await CapApp.addListener("backButton", ({ canGoBack }) => {
      if (canGoBack) {
        try { window.history.back(); } catch (_) { /* noop */ }
      } else if (typeof onBackAtRoot === "function") {
        onBackAtRoot();
      }
    });
  } catch (_) { /* noop */ }
  return () => {
    try { listener?.remove?.(); } catch (_) { /* noop */ }
  };
}
